if (!customElements.get('reveal-text')) {
  class RevealText extends HTMLElement {
    static get observedAttributes() {
      return ['reveal-start', 'reveal-end'];
    }

    constructor() {
      super();
      this.chars = [];
      this.targets = [];
      this.rafId = null;
      this.resizeObserver = null;
      this.revealStart = 0.88;
      this.revealEnd = 0.42;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.onScroll = () => this.scheduleUpdate();
      this.onResize = () => this.scheduleUpdate();
    }

    connectedCallback() {
      if (this.reduceMotion) {
        this.classList.add('is-ready', 'is-complete');
        return;
      }

      const run = () => {
        if (!this.isConnected || this._booted) return;
        this._booted = true;
        this.unwrapLegacyStructure();
        this.readOptions();
        this.collectTargets();
        this.splitWords();
        this.bind();
        this.scheduleUpdate();
      };

      // The parser connects <reveal-text> before its children exist.
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', run, { once: true });
        return;
      }

      run();
    }

    disconnectedCallback() {
      this._booted = false;
      this.unbind();
      if (this.rafId != null) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
    }

    attributeChangedCallback() {
      if (!this.isConnected || this.reduceMotion) return;
      this.readOptions();
      this.scheduleUpdate();
    }

    readOptions() {
      const start = Number(this.getAttribute('reveal-start'));
      const end = Number(this.getAttribute('reveal-end'));
      if (Number.isFinite(start)) this.revealStart = Math.min(1, Math.max(0, start));
      if (Number.isFinite(end)) this.revealEnd = Math.min(1, Math.max(0, end));
    }

    unwrapLegacyStructure() {
      const track = this.querySelector(':scope > .reveal-text-track');
      if (!track) return;

      const host = track.querySelector('.reveal-text-sticky') || track;
      [...host.childNodes].forEach((node) => this.appendChild(node));
      track.remove();
    }

    collectTargets() {
      this.targets = Array.from(this.querySelectorAll('[data-reveal-text]'));
    }

    wrapTextNodes(el) {
      Array.from(el.childNodes).forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const parts = node.textContent.split(/(\s+)/);
          const frag = document.createDocumentFragment();
          parts.forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) {
              frag.appendChild(document.createTextNode(part));
              return;
            }
            const word = document.createElement('span');
            word.className = 'reveal-text-word';
            Array.from(part).forEach((char) => {
              const span = document.createElement('span');
              span.className = 'reveal-text-char';
              span.textContent = char;
              word.appendChild(span);
            });
            frag.appendChild(word);
          });
          node.replaceWith(frag);
          return;
        }

        if (node.nodeType === Node.ELEMENT_NODE && node.tagName !== 'BR') {
          this.wrapTextNodes(node);
        }
      });
    }

    splitWords() {
      this.chars = [];
      this.targets.forEach((el) => {
        if (el.dataset.revealSplit === 'true') {
          this.chars.push(...el.querySelectorAll('.reveal-text-char'));
          return;
        }
        if (el._revealTextOriginal == null) {
          el._revealTextOriginal = el.innerHTML;
        }
        el.innerHTML = el._revealTextOriginal;
        const color = getComputedStyle(el).color;
        const channels = color.match(/[\d.]+/g);
        if (channels && channels.length >= 3) {
          el.style.setProperty('--reveal-fg', `${channels[0]}, ${channels[1]}, ${channels[2]}`);
        }
        this.wrapTextNodes(el);
        el.dataset.revealSplit = 'true';
        this.chars.push(...el.querySelectorAll('.reveal-text-char'));
      });
      this.chars.forEach((char) => char.style.setProperty('--reveal-progress', '0'));
      this.classList.add('is-ready');
    }

    getScrollY() {
      if (window.lenis && typeof window.lenis.scroll === 'number') {
        return window.lenis.scroll;
      }
      return window.scrollY || document.documentElement.scrollTop || 0;
    }

    getBlockProgress() {
      const el = this.targets[0] || this;
      const scrollY = this.getScrollY();
      const vh = window.innerHeight || 1;
      const top = el.getBoundingClientRect().top + scrollY;

      // Start when the heading reaches the lower part of the screen.
      // If it is already on screen at scroll 0, keep progress at 0 until
      // the user scrolls, so the first view stays faded.
      const travel = Math.max(280, Math.min(vh * 0.9, Math.max(1, this.chars.length) * 18));
      let start = top - vh * this.revealStart;
      if (start < 0) start = 0;

      return Math.min(1, Math.max(0, (scrollY - start) / travel));
    }

    update() {
      this.rafId = null;
      if (!this.chars.length) return;

      const progress = this.getBlockProgress();
      const count = this.chars.length;

      this.chars.forEach((char, index) => {
        const charProgress = Math.min(1, Math.max(0, progress * count - index));
        char.style.setProperty('--reveal-progress', String(charProgress));
      });

      if (progress >= 0.999) {
        this.classList.add('is-complete');
      } else {
        this.classList.remove('is-complete');
      }
    }

    scheduleUpdate() {
      if (this.rafId != null) return;
      this.rafId = requestAnimationFrame(() => this.update());
    }

    bind() {
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      if (window.lenis) window.lenis.on('scroll', this.onScroll);

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.scheduleUpdate());
        this.resizeObserver.observe(this);
        this.targets.forEach((el) => this.resizeObserver.observe(el));
      }
    }

    unbind() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      if (window.lenis) window.lenis.off('scroll', this.onScroll);
    }
  }

  customElements.define('reveal-text', RevealText);
}
